import React, { useEffect, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import Permissions from 'react-redux-permissions';
import { withRouter } from 'react-router';
import { compose } from 'recompose';

import { TextSearchField } from '@fd/ui/atoms/TextSearchField';
import PageLayout from '@fd/ui/Layout';

import * as appStoreActions from '../../../../../actions/appstore.actions';
import { flagService } from '../../../../../services';
import { AppStoreAppsList } from '../../../components/AppStoreAppsList';
import { AppStoreAppsListLoadingSkeleton } from '../../../components/AppStoreAppsListLoadingSkeleton';
import { AppStoreAppsListNoResults } from '../../../components/AppStoreAppsListNoResults';

type AppStoreProps = {};

const useStyles = makeStyles((theme: Theme) => ({
  searchField: {
    marginBottom: theme.spacing(3),
    width: '50%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
}));

type Props = AppStoreProps & MappedState & MappedDispatch;

const AppStoreApps = ({
  appId,
  isAppStoreOn,
  translate,
  getAppStoreApps,
  getConfiguredAppStoreApps,
  appStoreAppsLoading,
  configuredAppStoreAppsLoading,
  appStoreApps,
  totalRecordCount,
  configuredAppStoreAppIds,
}: Props) => {
  const classes = useStyles();
  const [page, setPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    getAppStoreApps(searchTerm, page);
  }, [searchTerm, page]);

  useEffect(() => {
    getConfiguredAppStoreApps(appId);
  }, [appId]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const renderContent = () => {
    if (appStoreAppsLoading || configuredAppStoreAppsLoading) {
      return <AppStoreAppsListLoadingSkeleton />;
    }
    if (appStoreApps) {
      if (appStoreApps.length === 0) {
        return <AppStoreAppsListNoResults />;
      }

      return (
        <AppStoreAppsList
          limit={30}
          onPageChange={handlePageChange}
          page={page}
          appStoreApps={appStoreApps}
          total={totalRecordCount}
          appId={appId}
          configuredAppStoreAppIds={configuredAppStoreAppIds}
        />
      );
    }
    return null;
  };

  const handleSearchTerm = (value: string) => {
    setSearchTerm(value);
  };

  if (!isAppStoreOn) {
    return null;
  }

  return (
    <PageLayout documentTitle="App_Store" title={translate('App_Store')}>
      <div>
        <Permissions allowed={['Owner']}>
          <TextSearchField
            className={classes.searchField}
            clearAriaLabel={`${translate('Search_for_App_Name')}`}
            dataFd="input-search"
            disableLabelAnimation={true}
            onChange={handleSearchTerm}
            placeholder={`${translate('Search_for_App_Name')}`}
            variant="outlined"
          />
          {renderContent()}
        </Permissions>
      </div>
    </PageLayout>
  );
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  const { currentApp, locale } = state;
  return {
    appId: currentApp.AppId!,
    isAppStoreOn: flagService.isFlagOn(state, 'appStore'),
    translate: getTranslate(locale),
    appStoreApps: state.appStore.appStoreApps,
    appStoreAppsLoading: state.appStore.appStoreAppsLoading,
    configuredAppStoreAppsLoading: state.appStore.configuredAppStoreAppsLoading,
    totalRecordCount: state.appStore.totalAppStoreAppRecords,
    configuredAppStoreAppIds: state.appStore.configuredAppStoreApps
      ? state.appStore.configuredAppStoreApps.map((configuration) => configuration.AppStoreAppId)
      : [],
  };
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  getAppStoreApps: (searchTerm: string, page: number) =>
    dispatch(appStoreActions.getAppStoreApps(searchTerm, page)),
  getConfiguredAppStoreApps: (appId: string) =>
    dispatch(appStoreActions.getConfiguredAppStoreAppsForApp(appId)),
});

const EnhancedComponent = compose<Props, any>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(AppStoreApps);

export default EnhancedComponent;
